import React from "react";
import type { OwnerMissionView } from "../api.js";
import type { OrbState } from "../orb/index.js";

export function ownerMissionOrbState(mission: OwnerMissionView | null): OrbState {
  if (!mission) return "idle";
  if (mission.phase === "executing") return "acting";
  if (mission.phase === "paused" || mission.phase === "awaiting_approval") return "confirming";
  if (mission.phase === "failed") return "error";
  if (mission.phase === "completed") return "speaking";
  return "idle";
}

/** OWNEXP Slice 4/9 — Phasen-/Statussprache Owner-lesbar; Rohcode im title. */
const PHASE_LABEL: Record<OwnerMissionView["phase"], string> = {
  executing: "Läuft gerade",
  paused: "Pausiert",
  awaiting_approval: "Wartet auf deine Entscheidung",
  completed: "Abgeschlossen",
  cancelled: "Abgebrochen",
  failed: "Gestört",
};

const STATUS_LABEL: Record<OwnerMissionView["status"], string> = {
  running: "läuft",
  awaiting_approval: "wartet auf Freigabe",
  paused: "pausiert",
  done: "fertig",
  cancelled: "abgebrochen",
  error: "Fehler",
};

/** OWNEXP Slice 9 (DC-33): Lauf-Fehler dreiteilig — Ursache/Wirkung/Ausweg.
 *  reale Failure-Fixture: owner_mission.degraded_service (Modell-Runtime
 *  nicht verfügbar). */
export function missionFailureCopy(mission: OwnerMissionView): { ursache: string; wirkung: string; ausweg: string } | null {
  if (mission.phase !== "failed" && mission.status !== "error") return null;
  const typ = mission.latestEventType ?? "";
  if (typ.includes("degraded_service") || typ.includes("degraded")) {
    return {
      ursache: "Die benötigte Modell-Runtime ist gerade nicht verfügbar (Dienst eingeschränkt).",
      wirkung: "Der Lauf wurde sicher beendet — keine halbfertige Arbeit, die Queue hält das Item zum Weiterlaufen bereit.",
      ausweg: "SIMO kann die Mission automatisch fortsetzen, sobald die Runtime wieder erreichbar ist (Fortsetzen drücken oder Queue-Item neu anstoßen).",
    };
  }
  if (typ.includes("stopped")) {
    return {
      ursache: "Du hast diese Mission gestoppt.",
      wirkung: "Keine automatische Fortsetzung; das Queue-Item bleibt zum Wiederaufnehmen bereit.",
      ausweg: "Über Fortsetzen erneut anstoßen oder in der Queue neu starten.",
    };
  }
  return {
    ursache: `Der Lauf wurde mit Fehler beendet${typ ? ` (${typ})` : ""}.`,
    wirkung: "Es läuft gerade nichts an dieser Mission — der Zustand ist in der Queue für einen sauberen Neustart gesichert.",
    ausweg: "Fehlerursache im Missions-Detail (Ereignisse/Evidence) nachvollziehen und neu anstoßen.",
  };
}

export function ownerMissionErrorCopy(raw: string | null): string | null {
  if (!raw) return null;
  if (/401|missing_session|unauthori[sz]ed|session.*expired/i.test(raw)) {
    return "Owner-Session ist abgelaufen oder nicht freigegeben. Bitte neu anmelden und den Mission-Status erneut laden.";
  }
  if (/403|forbidden|owner.*role/i.test(raw)) {
    return "Diese Mission-Aktion benötigt die freigegebene Owner-Rolle.";
  }
  if (/approval_pending|approval.*pending|Freigabe.*offen/i.test(raw)) {
    return "Die run-gebundene Freigabe ist noch offen. Fortsetzen bleibt bis zur bestätigten Owner-Freigabe gesperrt.";
  }
  return "Mission-Status ist derzeit nicht erreichbar. Bitte Live-Daten neu laden; technische Details bleiben in der serverseitigen Evidence.";
}

export function OwnerMissionControl({
  mission,
  busy,
  error,
  onAction,
}: {
  mission: OwnerMissionView | null;
  busy: boolean;
  error: string | null;
  onAction: (action: "pause" | "resume" | "stop") => void;
}): JSX.Element {
  const safeError = ownerMissionErrorCopy(error);
  if (!mission) {
    return (
      <section className="simo-owner-mission-control" aria-label="Owner Mission Control" data-phase="idle">
        <div className="simo-owner-mission-control-head">
          <span>Mission Control</span>
          <strong>Noch keine Owner-Mission</strong>
        </div>
        <p>Sprich oder schreib unten einen Auftrag. SIMO startet erst dann einen nativen AgentGraph-Run.</p>
        {safeError && <p className="simo-owner-mission-error" role="alert">{safeError}</p>}
      </section>
    );
  }

  const fehler = missionFailureCopy(mission);
  const phaseLabel = PHASE_LABEL[mission.phase] ?? mission.phase;
  const statusLabelWert = STATUS_LABEL[mission.status] ?? mission.status;

  return (
    <section className="simo-owner-mission-control" aria-label="Owner Mission Control" data-phase={mission.phase}>
      <div className="simo-owner-mission-control-head">
        <span title={mission.phase}>Mission Control · {phaseLabel}</span>
        <strong>{mission.goal}</strong>
        <small>{mission.runId} · {mission.eventCount} Mission-Events</small>
      </div>

      {/* OWNEXP Slice 9 (DC-33): Lauf-Fehler dreiteilig statt Rohcode-Wand */}
      {fehler && (
        <div className="simo-owner-mission-error" role="alert" data-testid="owner-mission-failure">
          <strong>MISSION GESTÖRT — {fehler.ursache}</strong>
          <span>{fehler.wirkung}</span>
          <span>{fehler.ausweg}</span>
        </div>
      )}

      <div className="simo-owner-mission-graph" aria-label="Live Agent constellation">
        <div className="simo-owner-mission-root">
          <span>SIMO</span>
          <strong title={mission.status}>{statusLabelWert}</strong>
          <small>{mission.runId}</small>
        </div>
        {mission.children.map((child) => (
          <article key={child.runId} className="simo-owner-mission-child" data-status={child.status}>
            <span>{child.provider ?? "Provider noch nicht gebunden"}</span>
            <strong>{child.phase} · {child.status}</strong>
            <small>{child.runId}</small>
            <div>
              <em>Provider-Events {child.providerEventsObserved ? "belegt" : "offen"}</em>
              <em>Tool-Events {child.toolEventsObserved ? "belegt" : "keine"}</em>
              <em>{child.eventCount} Ledger-Events</em>
            </div>
          </article>
        ))}
      </div>

      {mission.approvalIds.length > 0 && (
        <p className="simo-owner-mission-approval">Run-gebundene Freigabe: {mission.approvalIds.join(", ")}</p>
      )}
      <div className="simo-owner-mission-actions" aria-label="Mission process controls">
        {mission.controls.canPause && <button type="button" disabled={busy} onClick={() => onAction("pause")}>Pausieren</button>}
        {mission.controls.canResume && <button type="button" disabled={busy} onClick={() => onAction("resume")}>Fortsetzen</button>}
        {mission.controls.canStop && <button type="button" disabled={busy} onClick={() => onAction("stop")}>Stoppen</button>}
      </div>
      {safeError && <p className="simo-owner-mission-error" role="alert">{safeError}</p>}
      <small>Quelle: nativer AgentGraph, Common Harness, Run Ledger und run-gebundene Approvals. Keine erfundenen Knoten.</small>
    </section>
  );
}
